import type { Collector, Kring } from '@/types/odoo';
import { systemDot, systemLabel, useSystems } from '@/stores/systems';

/**
 * The kringen inside one collector, as the aannemer sees them when he opens the card.
 *
 * The m² per kring does not exist in Odoo — it is derived from the kring length share
 * by the API (portal-api/src/utils/kring-verdeling.ts). The "Per systeem" block below
 * the table carries the numbers that matter for money: each row is one line of his
 * onderaannemer-PO. The per-kring numbers add up to their own group exactly; the grand
 * total may sit a cent off the collector m², because the PO rounds per group too.
 */

const fmt = (n: number, decimals = 2): string =>
  n.toLocaleString('nl-BE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

// Legpatroon is a float (7.5, 7.125, 12.5 cm): show it as stored, no ",0" on whole values.
// 10 fraction digits keeps every typed value and only hides float noise (7.300000000000001).
const fmtLp = (n: number): string =>
  n.toLocaleString('nl-BE', { minimumFractionDigits: 0, maximumFractionDigits: 10 });

function KringM2Cell({ kring }: { kring: Kring }) {
  if (typeof kring.m2 === 'number') return <>{fmt(kring.m2)} m²</>;
  if (kring.reden === 'geen_legpatroon') {
    return <span className="text-amber-600">legpatroon ontbreekt</span>;
  }
  return <span className="text-td-muted">—</span>;
}

export function InstallerKringenTable({ collector }: { collector: Collector }) {
  // Subscribing loads the Odoo-defined roster, so systemLabel/systemDot resolve.
  useSystems();

  const kringen = collector.kringen ?? [];
  const verdeling = collector.kringVerdeling ?? [];

  if (kringen.length === 0) {
    return (
      <div>
        <h4 className="text-xs font-bold text-td-navy mb-1">Kringen</h4>
        <p className="text-xs text-td-muted">Geen kringen ingegeven</p>
      </div>
    );
  }

  const geenM2 = kringen.some(k => k.reden === 'geen_m2');
  const geenLengte = !geenM2 && kringen.some(k => k.reden === 'geen_lengte');
  const totaleLengte = kringen.reduce((s, k) => s + (k.x_length || 0), 0);

  return (
    <div>
      <h4 className="text-xs font-bold text-td-navy mb-2">Kringen</h4>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] font-bold text-td-muted uppercase tracking-wide">
              <th className="py-1 pr-2 w-8">#</th>
              <th className="py-1 pr-2">Systeem</th>
              <th className="py-1 pr-2 text-right whitespace-nowrap">Legpatroon</th>
              <th className="py-1 pr-2 text-right whitespace-nowrap">Lengte</th>
              <th className="py-1 text-right whitespace-nowrap">m²</th>
            </tr>
          </thead>
          <tbody>
            {kringen.map((k) => (
              <tr key={k.id} className="border-t border-gray-100">
                <td className="py-1 pr-2 font-semibold text-td-navy">{k.x_kringnummer}</td>
                <td className="py-1 pr-2">
                  <span className="inline-flex items-center gap-1.5 min-w-0">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${systemDot(k.x_system || '')}`} aria-hidden="true" />
                    <span className="truncate">{systemLabel(k.x_system || '') || '—'}</span>
                  </span>
                </td>
                <td className="py-1 pr-2 text-right whitespace-nowrap">
                  {k.x_legpatroon > 0 ? `${fmtLp(k.x_legpatroon)} cm` : <span className="text-td-muted">—</span>}
                </td>
                <td className="py-1 pr-2 text-right whitespace-nowrap">
                  {k.x_length > 0 ? `${fmt(k.x_length, 0)} m` : <span className="text-td-muted">—</span>}
                </td>
                <td className="py-1 text-right whitespace-nowrap font-semibold text-td-navy">
                  <KringM2Cell kring={k} />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-gray-200 text-td-muted">
              <td className="py-1 pr-2" />
              <td className="py-1 pr-2 font-bold">Totaal</td>
              <td className="py-1 pr-2" />
              <td className="py-1 pr-2 text-right font-bold whitespace-nowrap">{fmt(totaleLengte, 0)} m</td>
              <td className="py-1 text-right font-bold whitespace-nowrap">
                {typeof collector.kringTotaalM2 === 'number' && !geenM2
                  ? `${fmt(collector.kringTotaalM2)} m²`
                  : '—'}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {geenM2 && (
        <p className="text-[11px] text-amber-600 mt-1.5">Geen m² op deze collector</p>
      )}
      {geenLengte && (
        <p className="text-[11px] text-td-muted mt-1.5">
          m² per kring niet te berekenen zonder kringlengtes
        </p>
      )}

      {verdeling.length > 0 && !geenM2 && (
        <div className="mt-3 pt-2 border-t border-gray-100">
          <h5 className="text-[10px] font-bold text-td-muted uppercase tracking-wide mb-1">Per systeem</h5>
          <ul className="space-y-0.5">
            {verdeling.map((g) => (
              <li key={`${g.systeem}|${g.legpatroon}`} className="flex items-center justify-between gap-2 text-xs">
                <span className="inline-flex items-center gap-1.5 min-w-0">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${systemDot(g.systeem)}`} aria-hidden="true" />
                  <span className="truncate">
                    {systemLabel(g.systeem) || g.systeem} · {fmtLp(g.legpatroon)} cm
                  </span>
                </span>
                <span className="shrink-0 text-td-muted whitespace-nowrap">
                  {fmt(g.lengte, 0)} m · <span className="font-semibold text-td-navy">{fmt(g.m2)} m²</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
